<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <h2>Pacchetti</h2>
        <v-btn color="primary" size="small" @click="$router.push('/kits/new')">Nuovo</v-btn>
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
      <v-col cols="12">
        <v-row>
          <v-col cols="12" sm="6" md="4" lg="2">
            <v-text-field  v-model="query.title" label="Titolo" clearable></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="2">
            <v-select  v-model="query.ctg" label="Categoria" clearable :items="categoryOptions"></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="2">
            <v-select  v-model="query.genre" label="Genere" clearable :items="genreOptions"></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="2">
            <v-select  v-model="query.diff" label="Difficoltà" clearable :items="difficultyOptions"></v-select>
          </v-col>

        </v-row>
      </v-col>
      <v-col cols="12">
        <data-table :headers="headers" collection="kits" view-sub-path="" :editable="true" deletable :query="query" @error="onListError">
          <template v-slot:item.icon.url="{ item }">
            <v-img :src="item.icon ? item.icon.url : ''" width="50" height="50" contain color="grey-darken-1" />
          </template>
          <template v-slot:item.title.it="{ item }">
            <a @click="$router.push('/kits/'+item._id)"  class="text-decoration-underline cursor-pointer" ><b>{{ item.title.it }}</b></a>
          </template>
          <template v-slot:item.category.name.it="{ item }">
            <a v-if="item.category" @click="$router.push('/categories/'+item.category._id)" class="text-decoration-underline cursor-pointer" >{{ item.category.name.it }}</a>
          </template>
          <template v-slot:item.genre.name.it="{ item }">
            <a v-if="item.genre" @click="$router.push('/genre/'+item.genre._id)"  class="text-decoration-underline cursor-pointer" >{{ item.genre.name.it }}</a>
          </template>
          <template v-slot:item.difficulty.name.it="{ item }">
            <a v-if="item.difficulty" @click="$router.push('/difficulties/'+item.difficulty._id)"  class="text-decoration-underline cursor-pointer" >{{ item.difficulty.name.it }}</a>
          </template>

          <template v-slot:item.pub="{ item }">
            <strong class="text-success" v-if="item.pub">Sì</strong>
            <span class="text-error" v-else>No</span>
          </template>
          <template v-slot:item.products="{ item }">
            <v-btn color="primary" variant="text" icon="mdi-cart" @click="$router.push(`/kits/${item._id}/products`)"></v-btn>
          </template>
          <template v-slot:item.clone="{ item }">
            <v-btn color="primary" variant="text" icon="mdi-content-copy" @click="requestClone(item._id)"></v-btn>
          </template>

        </data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import {defineComponent, ref, computed, watch, onMounted, inject, onBeforeUnmount} from 'vue'
import { useRouter } from 'vue-router'
import utils from '../utils'
import DataTable from "@/components/DataTable.vue";
import {type DataTableError, type Kit, type Query, UserRole} from "@/models.ts";
import {type Messages, Msg} from "@/plugins/messages.ts";
import {roles} from "@/consts.ts";
import type {Auth} from "@/plugins/auth.ts";
import type {Globals} from "@/plugins/globals.ts";
import config from "@/config.ts";
import api from "@/api";


export default defineComponent({
  name: "Kits",
  components: {DataTable},
  setup() {
    const auth = inject('auth') as Auth
    const messages = inject('messages') as Messages
    const globals = inject('globals') as Globals
    const router = useRouter()

    const query = ref<Query>({})


    let wh:any;
    onMounted(async ()=>{
      await auth.waitForLogin
      await globals.waitForInit

      await Promise.all([
        globals.refresh('categories'),
        globals.refresh('difficulties'),
        globals.refresh('genre'),
      ])
      query.value = utils.getSessionQuery(`kits`)
      wh = watch(query.value, ()=>{
        utils.setSessionQuery("kits", query.value)
      }, { deep: true })
    })
    onBeforeUnmount(()=>{
      if(wh) wh();
    })
    return {
      UserRole,
      roles,
      formatDateTime : utils.formatDateTime,
      query,
      headers : [
        { title : 'Ord.', sortable: true, align: 'left', value : 'order', width: 60 },
        { title : 'Icona', sortable: false, align: 'left', value : 'icon.url' },
        { title : 'Titolo', sortable: true, align: 'left', value : 'title.it' },
        { title : 'Categoria', sortable: true, align: 'left', value : 'category.name.it' },
        { title : 'Genere', sortable: true, align: 'left', value : 'genre.name.it' },
        { title : 'Difficoltà', sortable: true, align: 'left', value : 'difficulty.name.it' },
        { title : 'Pubblicato', sortable: true, align: 'left', value : 'pub' },
        { title : 'Prodotti', sortable: false, align: 'left', value : 'products', width : 60 },
        { title : 'Clona', sortable: false, align: 'left', value : 'clone', width : 60 },
      ],
      onListError(e:DataTableError) {
        if(e.status === 401) {
          messages.error(Msg.permissionError)
          auth.logout()
          return
        }
        messages.error(`Errore: ${e.message}`)
      },
      categoryOptions : computed(()=>{
        return globals.categories.value.map(i => {
          return { title : i.name.it, value : i._id }
        })
      }),
      genreOptions : computed(()=>{
        return globals.genre.value.map(i => {
          return { title : i.name.it, value : i._id }
        })
      }),
      difficultyOptions : computed(()=>{
        return globals.difficulties.value.map(i => {
          return { title : i.name.it, value : i._id }
        })
      }),
      requestClone( id:string ) {
        messages.requestConfirm('Richiesta clonazione', 'Sei sicuro di voler duplicare questo elemento?', false,  async ( accepted:boolean )=>{
          if(!accepted) return
          const res = await api.get<Kit>('kits', `${id}/duplicate`)
          if(!res.success) {
            messages.error(res.message ?? Msg.serverError)
          } else {
            messages.success("Elemento duplicato!")
            await router.push(`/kits/${res.data?._id}`)
          }

        });
      },
    }
  }
})
</script>
